import { useState } from 'react';
import { FlavorTile } from './FlavorTile';
import { GasCylinderIcon, PickupTruckIcon } from './CategoryIcons';
import { cn } from '@/lib/utils';
import type { Category } from '@/types/domain';

interface ProductImageProps {
  imageUrl: string | null;
  accent: string;
  category: Category;
  alt: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
  /** Disable the hover-zoom. Defaults to enabled on `lg`, off on others. */
  zoomOnHover?: boolean;
}

const SIZE_PX: Record<NonNullable<ProductImageProps['size']>, number> = {
  xs: 32,
  sm: 40,
  md: 56,
  lg: 96, // bumped from 80
};

const ICON_PX: Record<NonNullable<ProductImageProps['size']>, number> = {
  xs: 18,
  sm: 22,
  md: 32,
  lg: 56, // bumped to match new tile size
};

/**
 * ProductImage — smart image renderer for catalog products.
 *
 * Fallback tree:
 *   1. `<img>` if `imageUrl` is set and loads.
 *   2. CategoryIcons (Gas → cylinder, Other → pickup truck) for those
 *      categories.
 *   3. FlavorTile (accent band) for everything else.
 *
 * On `lg` size, the tile pops to 1.6× on hover via CSS transform so the
 * customer can see the seal artwork without opening the Specs drawer.
 */
export function ProductImage({
  imageUrl,
  accent,
  category,
  alt,
  size = 'md',
  className,
  zoomOnHover,
}: ProductImageProps) {
  const [errored, setErrored] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const px = SIZE_PX[size];
  const allowZoom = zoomOnHover ?? size === 'lg';

  // Frame styles: hover scale + lift via shadow + raise z to escape the card.
  // The wrapper is `relative` so the absolute z lift doesn't clip.
  const wrapperClasses = cn(
    'relative shrink-0 rounded-lg overflow-hidden border border-brix-border bg-brix-surface-alt',
    allowZoom &&
      'transition-transform duration-200 ease-out hover:scale-[1.6] hover:z-20 hover:shadow-card-hover hover:border-brix-primary/40 origin-center',
    className,
  );

  if (!imageUrl || errored) {
    return (
      <CategoryFallbackTile
        category={category}
        accent={accent}
        size={size}
        className={className}
        allowZoom={allowZoom}
      />
    );
  }

  return (
    <div className={wrapperClasses} style={{ width: px, height: px }}>
      {!loaded && (
        <div
          aria-hidden
          className="absolute inset-0 animate-pulse"
          style={{ background: `${accent}1A` }}
        />
      )}
      <img
        src={imageUrl}
        alt={alt}
        loading="lazy"
        decoding="async"
        onLoad={() => setLoaded(true)}
        onError={() => setErrored(true)}
        className={cn(
          'h-full w-full object-contain p-1 transition-opacity duration-200',
          loaded ? 'opacity-100' : 'opacity-0',
        )}
      />
    </div>
  );
}

function CategoryFallbackTile({
  category,
  accent,
  size,
  className,
  allowZoom,
}: {
  category: Category;
  accent: string;
  size: NonNullable<ProductImageProps['size']>;
  className?: string;
  allowZoom: boolean;
}) {
  const px = SIZE_PX[size];
  const ipx = ICON_PX[size];

  const hoverClasses =
    allowZoom &&
    'transition-transform duration-200 ease-out hover:scale-[1.6] hover:z-20 hover:shadow-card-hover hover:border-brix-primary/40 origin-center';

  if (category === 'Gas') {
    return (
      <div
        className={cn(
          'relative shrink-0 rounded-lg overflow-hidden border border-brix-border bg-brix-primary-tint flex items-center justify-center text-brix-primary',
          hoverClasses,
          className,
        )}
        style={{ width: px, height: px }}
        aria-hidden
      >
        <GasCylinderIcon size={ipx} />
      </div>
    );
  }

  if (category === 'Other') {
    return (
      <div
        className={cn(
          'relative shrink-0 rounded-lg overflow-hidden border border-brix-border bg-brix-accent/20 flex items-center justify-center text-brix-primary',
          hoverClasses,
          className,
        )}
        style={{ width: px, height: px }}
        aria-hidden
      >
        <PickupTruckIcon size={ipx} />
      </div>
    );
  }

  const tileSize: 'sm' | 'md' = size === 'xs' || size === 'sm' ? 'sm' : 'md';
  return <FlavorTile accent={accent} category={category} size={tileSize} className={className} />;
}
